import type {
  TripFiltersPayload,
  TripPayload,
  TripTagOption,
  WizardValues,
} from './types';

/** Selected pills → the `{ id, displayName }` shape the composer filters on. */
export function tagsToFilters(
  tags: TripTagOption[],
): { id: string; displayName: string }[] {
  return tags.map((t) => ({ id: t.id, displayName: t.label }));
}

/**
 * The tripFilters jsonb bag: brief, themes, styles and the per-room split.
 * Persisted verbatim by the API and read back by the composer (which pulls
 * `.id` off themes/styles and reads `composition.roomsComposition`). Shared by
 * buildTripPayload (persisted copy) and each host's onSaved (live compose copy).
 */
export function buildTripFilters(values: WizardValues): TripFiltersPayload {
  const childrenCount = Math.max(0, values.childrenBirthdates?.length ?? 0);
  // KD-3758 — the travellers step keeps every birthdate on the first room and
  // the split step only moves counts, so each room takes its own children's
  // birthdates here, in order. Left on the first room, a trip of 2 rooms of
  // 1 adult + 1 child priced hotels as a room of 3 and a room of 1.
  let nextChild = 0;
  const roomsComposition = values.rooms.map((room) => {
    const childrenBirthdates = Array.from(
      { length: room.children ?? 0 },
      (_, i) => values.childrenBirthdates?.[nextChild + i] ?? null,
    );
    nextChild += room.children ?? 0;
    return { ...room, childrenBirthdates };
  });
  return {
    themes: tagsToFilters(values.themes),
    styles: tagsToFilters(values.hotelStyles),
    brief: values.brief,
    composition: {
      adults: values.adultPax,
      children: childrenCount,
      childrenBirthdates: Array.from(
        { length: childrenCount },
        (_, i) => values.childrenBirthdates?.[i] ?? null,
      ),
      count: values.adultPax + childrenCount,
      roomsComposition,
    },
  };
}

/** Date at noon today — matches the date picker's noon convention. */
function todayNoon(): Date {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * True for a departure date the API would refuse: null, invalid, or before
 * today. Trip hydration uses it to blank a stale stored date so the wizard's
 * dates step forces the user to pick a fresh one, like the from-scratch flow.
 */
export function isInvalidDepartureDate(
  date: Date | null | undefined,
): date is null | undefined {
  return (
    !date ||
    isNaN(date.getTime()) ||
    startOfDay(date).getTime() < startOfDay(new Date()).getTime()
  );
}

/**
 * A departure date the API would refuse is replaced by today at noon; a valid
 * future date passes through untouched. Shared by buildTripPayload (persisted
 * copy), useTripWizard's onSaved values (live compose copy) and the
 * tripFilters localStorage restore (mid-compose recovery, where there is no
 * wizard left to ask the user).
 */
export function clampDepartureDate(date: Date | null | undefined): Date {
  return isInvalidDepartureDate(date) ? todayNoon() : date;
}

/**
 * Builds the /trip/save payload from wizard answers.
 * Ported from ScratchTripForm: a departure before today is clamped to today,
 * children birthdates are normalized to exactly `children count` entries.
 * brief, themes, hotelStyles and rooms ride in the tripFilters jsonb bag
 * (see buildTripFilters) — the API persists it verbatim and Compose reads it.
 */
export function buildTripPayload(
  values: WizardValues,
  opts: { dmcId: string | null; locale: string; id?: string },
): TripPayload {
  const childrenCount = Math.max(0, values.childrenBirthdates?.length ?? 0);
  const childrenBirthdates = Array.from(
    { length: childrenCount },
    (_, i) => values.childrenBirthdates?.[i] ?? null,
  );

  const selectedDate = clampDepartureDate(values.departureDate);

  return {
    ...(opts.id ? { id: opts.id } : {}),
    name: values.name,
    countries: values.countries.map((c) => ({ id: c.value })),
    startLocation: values.startLocation || undefined,
    endLocation: values.endLocation || undefined,
    startDate: selectedDate,
    departureDate: selectedDate,
    duration: values.duration,
    dmcId: opts.dmcId,
    adultPax: values.adultPax,
    childrenBirthdates,
    locale: opts.locale,
    tripFilters: buildTripFilters(values),
  };
}

/** Sum of end date from a departure + duration (for the "22 Jul → 28 Jul" hint). */
export function computeEndDate(
  departure: Date | null,
  duration: number,
): Date | null {
  if (!departure || !duration) return null;
  const end = new Date(departure);
  end.setDate(end.getDate() + Math.max(0, duration - 1));
  return end;
}
